import { ReportTranslationKeys } from 'intl/keys';
import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import {
  IssueModel,
  AttachmentCounts,
} from 'shared/domain/issue/issueModel';
import { DocumentsOnIssueView } from 'presentation/document/documentsOnIssueView';
import { DocumentOnView } from 'presentation/document/documentOnView';
import { buildReportIssueStatsOverridesFromDocuments } from 'shared/domain/report/estimate/reportIssueStats';
import {
  buildReportIssueEstimateRows,
  ReportIssueEstimateRow,
} from 'shared/domain/report/estimate/reportIssueEstimateRow';
import { ReportType } from 'shared/domain/report/types/model/common';
import { isInspectionReportType } from 'shared/domain/report/types/model/reportTypeGuards';
import {
  MessageSectionConfigOnView,
  SectionConfigOnView,
} from 'shared/domain/report/types/onView/common';

export enum ReportSectionId {
  issueAttachments = 'issueAttachments',
  linkedDocumentation = 'linkedDocumentation',
  events = 'events',
  incomingEmailMessages = 'incomingEmailMessages',
  workflowDecisions = 'workflowDecisions',
  inspectionInfo = 'inspectionInfo',
  summary = 'summary',
  checklist = 'checklist',
  issueRegister = 'issueRegister',
  signatures = 'signatures',
  generalStandard = 'generalStandard',
  generalStandardAttachments = 'generalStandardAttachments',
  sectionDescriptions = 'sectionDescriptions',
  sectionAttachments = 'sectionAttachments',
  controlledElementStandard = 'controlledElementStandard',
  controlledElementStandardAttachments = 'controlledElementStandardAttachments',
  controlledElementAttachments = 'controlledElementAttachments',
}

export const ISSUE_REPORT_SECTION_IDS: ReportSectionId[] = [
  ReportSectionId.issueAttachments,
  ReportSectionId.linkedDocumentation,
  ReportSectionId.events,
  ReportSectionId.incomingEmailMessages,
  ReportSectionId.workflowDecisions,
];

export const INSPECTION_REPORT_SECTION_IDS: ReportSectionId[] = [
  ReportSectionId.inspectionInfo,
  ReportSectionId.summary,
  ReportSectionId.checklist,
  ReportSectionId.issueRegister,
  ReportSectionId.signatures,
  ReportSectionId.generalStandard,
  ReportSectionId.generalStandardAttachments,
  ReportSectionId.sectionDescriptions,
  ReportSectionId.sectionAttachments,
  ReportSectionId.controlledElementStandard,
  ReportSectionId.controlledElementStandardAttachments,
  ReportSectionId.controlledElementAttachments,
];

export const REPORT_SECTION_IDS: ReportSectionId[] = [
  ...ISSUE_REPORT_SECTION_IDS,
  ...INSPECTION_REPORT_SECTION_IDS,
];

export type SectionAvailability = {
  available: boolean;
  unavailableReasonId?: string;
};

export type ReportSectionAvailabilityMap = Record<
  ReportSectionId,
  SectionAvailability
>;

type IssueReportSectionId =
  | ReportSectionId.issueAttachments
  | ReportSectionId.linkedDocumentation
  | ReportSectionId.events
  | ReportSectionId.incomingEmailMessages
  | ReportSectionId.workflowDecisions;

const MULTI_UNAVAILABLE_REASON_IDS: Record<IssueReportSectionId, string> = {
  [ReportSectionId.issueAttachments]:
    ReportTranslationKeys.sectionDisabledIssueAttachments,
  [ReportSectionId.linkedDocumentation]:
    ReportTranslationKeys.sectionDisabledLinkedDocumentation,
  [ReportSectionId.events]: ReportTranslationKeys.messageKindDisabledEvents,
  [ReportSectionId.incomingEmailMessages]:
    ReportTranslationKeys.messageKindDisabledIncomingEmailMessages,
  [ReportSectionId.workflowDecisions]:
    ReportTranslationKeys.messageKindDisabledWorkflowDecisions,
};

const SINGLE_UNAVAILABLE_REASON_IDS: Record<IssueReportSectionId, string> = {
  [ReportSectionId.issueAttachments]:
    ReportTranslationKeys.sectionDisabledIssueAttachmentsSingle,
  [ReportSectionId.linkedDocumentation]:
    ReportTranslationKeys.sectionDisabledLinkedDocumentationSingle,
  [ReportSectionId.events]:
    ReportTranslationKeys.sectionDisabledEventsSingle,
  [ReportSectionId.incomingEmailMessages]:
    ReportTranslationKeys.sectionDisabledIncomingEmailMessagesSingle,
  [ReportSectionId.workflowDecisions]:
    ReportTranslationKeys.sectionDisabledWorkflowDecisionsSingle,
};

function getUnavailableReasonIds(
  isSingleIssue: boolean
): Record<IssueReportSectionId, string> {
  return isSingleIssue
    ? SINGLE_UNAVAILABLE_REASON_IDS
    : MULTI_UNAVAILABLE_REASON_IDS;
}

function buildAvailabilityMap(
  entries: Partial<ReportSectionAvailabilityMap>
): ReportSectionAvailabilityMap {
  const map = {} as ReportSectionAvailabilityMap;
  for (const sectionId of REPORT_SECTION_IDS) {
    map[sectionId] = entries[sectionId] ?? { available: true };
  }
  return map;
}

export function allSectionsAvailable(): ReportSectionAvailabilityMap {
  return buildAvailabilityMap({});
}

export function allSectionsUnavailable(
  isSingleIssue: boolean
): ReportSectionAvailabilityMap {
  const reasonIds = getUnavailableReasonIds(isSingleIssue);

  return buildAvailabilityMap({
    [ReportSectionId.issueAttachments]: {
      available: false,
      unavailableReasonId: reasonIds[ReportSectionId.issueAttachments],
    },
    [ReportSectionId.linkedDocumentation]: {
      available: false,
      unavailableReasonId: reasonIds[ReportSectionId.linkedDocumentation],
    },
    [ReportSectionId.events]: {
      available: false,
      unavailableReasonId: reasonIds[ReportSectionId.events],
    },
    [ReportSectionId.incomingEmailMessages]: {
      available: false,
      unavailableReasonId: reasonIds[ReportSectionId.incomingEmailMessages],
    },
    [ReportSectionId.workflowDecisions]: {
      available: false,
      unavailableReasonId: reasonIds[ReportSectionId.workflowDecisions],
    },
  });
}

export function areAvailabilityMapsEqual(
  left: ReportSectionAvailabilityMap,
  right: ReportSectionAvailabilityMap
): boolean {
  return REPORT_SECTION_IDS.every(
    (sectionId) =>
      left[sectionId].available === right[sectionId].available &&
      left[sectionId].unavailableReasonId ===
        right[sectionId].unavailableReasonId
  );
}

function activeMessagesCount(
  messages: ReadonlyArray<{ deleted?: boolean }> | undefined
): number {
  return messages?.filter((message) => !message.deleted).length ?? 0;
}

function decisionsCount(issue: IssueOnSingleView): number {
  return issue.workflowDecisions?.length ?? 0;
}

function linkedDocumentationCount(issue: IssueOnSingleView): number {
  return issue.linkedDocumentation?.length ?? 0;
}

function issueHasAttachments(
  issue: IssueOnSingleView,
  issueDocumentsCount: number
): boolean {
  return issueDocumentsCount > 0 || Boolean(issue.mainImage);
}

export function computeReportSectionAvailability(
  issues: IssueOnSingleView[],
  isSingleIssue: boolean,
  issueDocumentsCount = 0,
  /** List API models — table view clears `events` / `incomingEmailMessages`. */
  issueModels?: IssueModel[]
): ReportSectionAvailabilityMap {
  if (issues.length === 0) {
    return allSectionsUnavailable(isSingleIssue);
  }

  const reasonIds = getUnavailableReasonIds(isSingleIssue);

  const documentsCountForIssue = isSingleIssue ? issueDocumentsCount : 0;

  const anyAttachments = isSingleIssue
    ? issueHasAttachments(issues[0], documentsCountForIssue)
    // TODO PT-6831: primaryData.documents are not stored in IndexedDB, so only
    // mainImage is available as an attachment indicator for list-view issues.
    // Always treat attachments as available for multi-issue until document
    // counts are persisted per issue in the SW entity.
    : true;

  const anyLinkedDocumentation = issues.some(
    (issue) => linkedDocumentationCount(issue) > 0
  );
  const modelsForMessages =
    issueModels && issueModels.length > 0 ? issueModels : undefined;
  const anyEvents = modelsForMessages
    ? modelsForMessages.some((issue) => activeMessagesCount(issue.events) > 0)
    : issues.some((issue) => activeMessagesCount(issue.events) > 0);
  const anyEmails = modelsForMessages
    ? modelsForMessages.some(
        (issue) => activeMessagesCount(issue.incomingEmailMessages) > 0
      )
    : issues.some(
        (issue) => activeMessagesCount(issue.incomingEmailMessages) > 0
      );
  const anyDecisions = issues.some((issue) => decisionsCount(issue) > 0);

  const unavailable = (
    sectionId: IssueReportSectionId,
    available: boolean
  ): SectionAvailability =>
    available
      ? { available: true }
      : {
          available: false,
          unavailableReasonId: reasonIds[sectionId],
        };

  return buildAvailabilityMap({
    [ReportSectionId.issueAttachments]: unavailable(
      ReportSectionId.issueAttachments,
      anyAttachments
    ),
    [ReportSectionId.linkedDocumentation]: unavailable(
      ReportSectionId.linkedDocumentation,
      anyLinkedDocumentation
    ),
    [ReportSectionId.events]: unavailable(ReportSectionId.events, anyEvents),
    [ReportSectionId.incomingEmailMessages]: unavailable(
      ReportSectionId.incomingEmailMessages,
      anyEmails
    ),
    [ReportSectionId.workflowDecisions]: unavailable(
      ReportSectionId.workflowDecisions,
      anyDecisions
    ),
  });
}

const INSPECTION_CONTENT_SECTION_IDS: ReportSectionId[] = [
  ...INSPECTION_REPORT_SECTION_IDS,
  ReportSectionId.issueAttachments,
];

export function getReportContentSectionIds(
  reportType: ReportType
): ReportSectionId[] {
  return isInspectionReportType(reportType)
    ? INSPECTION_CONTENT_SECTION_IDS
    : ISSUE_REPORT_SECTION_IDS;
}

export function hasAnyReportContentSectionAvailable(
  availability: ReportSectionAvailabilityMap,
  reportType: ReportType
): boolean {
  return getReportContentSectionIds(reportType).some(
    (sectionId) => availability[sectionId].available
  );
}

export function sectionUnavailableProps(section: SectionAvailability): {
  unavailable?: boolean;
  unavailableReasonId?: string;
} {
  return section.available
    ? {}
    : {
        unavailable: true,
        unavailableReasonId: section.unavailableReasonId,
      };
}

export function gatedSectionAvailability(
  gate: boolean,
  section: SectionAvailability,
  gateUnavailableReasonId?: string
): SectionAvailability {
  if (!gate) {
    return {
      available: false,
      unavailableReasonId:
        gateUnavailableReasonId ?? section.unavailableReasonId,
    };
  }

  return { ...section };
}

export function disableSectionConfig(
  config: SectionConfigOnView
): SectionConfigOnView {
  return { ...config, enabled: false };
}

export function disableMessageSectionConfig(
  config: MessageSectionConfigOnView
): MessageSectionConfigOnView {
  return {
    ...config,
    enabled: false,
    attachments: { ...config.attachments, enabled: false },
  };
}

export function shouldDisableSection(
  config: SectionConfigOnView | undefined,
  available: boolean
): config is SectionConfigOnView {
  return Boolean(config && !available && config.enabled);
}

export function shouldDisableMessageSection(
  config: MessageSectionConfigOnView | undefined,
  available: boolean
): config is MessageSectionConfigOnView {
  return Boolean(
    config &&
      !available &&
      (config.enabled || config.attachments.enabled)
  );
}

export type ReportMessageSectionId =
  | ReportSectionId.events
  | ReportSectionId.incomingEmailMessages
  | ReportSectionId.workflowDecisions;

export const REPORT_MESSAGE_SECTION_IDS: ReportMessageSectionId[] = [
  ReportSectionId.events,
  ReportSectionId.incomingEmailMessages,
  ReportSectionId.workflowDecisions,
];

export type ReportMessageAttachmentsAvailabilityMap = Record<
  ReportMessageSectionId,
  SectionAvailability
>;

const MESSAGE_ATTACHMENTS_DISABLED_REASON_IDS: Record<
  ReportMessageSectionId,
  string
> = {
  [ReportSectionId.events]:
    ReportTranslationKeys.messageAttachmentsDisabledEvents,
  [ReportSectionId.incomingEmailMessages]:
    ReportTranslationKeys.messageAttachmentsDisabledIncomingEmailMessages,
  [ReportSectionId.workflowDecisions]:
    ReportTranslationKeys.messageAttachmentsDisabledWorkflowDecisions,
};

function messageAttachmentsAvailability(
  sectionId: ReportMessageSectionId,
  available: boolean
): SectionAvailability {
  return available
    ? { available: true }
    : {
        available: false,
        unavailableReasonId: MESSAGE_ATTACHMENTS_DISABLED_REASON_IDS[sectionId],
      };
}

export function allMessageAttachmentsAvailable(): ReportMessageAttachmentsAvailabilityMap {
  return {
    [ReportSectionId.events]: { available: true },
    [ReportSectionId.incomingEmailMessages]: { available: true },
    [ReportSectionId.workflowDecisions]: { available: true },
  };
}

export function areMessageAttachmentsMapsEqual(
  left: ReportMessageAttachmentsAvailabilityMap,
  right: ReportMessageAttachmentsAvailabilityMap
): boolean {
  return REPORT_MESSAGE_SECTION_IDS.every(
    (sectionId) =>
      left[sectionId].available === right[sectionId].available &&
      left[sectionId].unavailableReasonId ===
        right[sectionId].unavailableReasonId
  );
}

function mapHasAnyDocuments(
  documentsByParent: Record<string, DocumentOnView[]>
): boolean {
  return Object.values(documentsByParent).some((docs) => docs.length > 0);
}

/**
 * Single-issue source. Event/email attachments live in the document store maps keyed by
 * parent id; workflow decisions never carry attachments (backend renders them as text only).
 */
export function computeMessageAttachmentsAvailabilityFromDocuments(
  documents: DocumentsOnIssueView
): ReportMessageAttachmentsAvailabilityMap {
  return {
    [ReportSectionId.events]: messageAttachmentsAvailability(
      ReportSectionId.events,
      mapHasAnyDocuments(documents.eventDocuments)
    ),
    [ReportSectionId.incomingEmailMessages]: messageAttachmentsAvailability(
      ReportSectionId.incomingEmailMessages,
      mapHasAnyDocuments(documents.incomingEmailMessageDocuments)
    ),
    [ReportSectionId.workflowDecisions]: messageAttachmentsAvailability(
      ReportSectionId.workflowDecisions,
      false
    ),
  };
}

type MessageWithAttachmentCounts = {
  deleted?: boolean;
  attachmentCounts?: AttachmentCounts;
};

function messageHasAttachments(
  message: MessageWithAttachmentCounts
): boolean {
  const counts = message.attachmentCounts;
  return (
    !message.deleted &&
    counts !== undefined &&
    counts.images + counts.nonImages > 0
  );
}

function anyMessageHasAttachments(
  messages: ReadonlyArray<MessageWithAttachmentCounts> | undefined
): boolean {
  return (messages ?? []).some(messageHasAttachments);
}

/**
 * Multi-issue source. Reads the compact per-message {@link AttachmentCounts}
 * carried on events/emails in the filtered-issues payload (the worker derives them from
 * the persisted documents); workflow decisions never carry attachments.
 */
export function computeMessageAttachmentsAvailabilityFromModels(
  issueModels: IssueModel[]
): ReportMessageAttachmentsAvailabilityMap {
  const anyEventAttachments = issueModels.some((issue) =>
    anyMessageHasAttachments(issue.events)
  );
  const anyEmailAttachments = issueModels.some((issue) =>
    anyMessageHasAttachments(issue.incomingEmailMessages)
  );

  return {
    [ReportSectionId.events]: messageAttachmentsAvailability(
      ReportSectionId.events,
      anyEventAttachments
    ),
    [ReportSectionId.incomingEmailMessages]: messageAttachmentsAvailability(
      ReportSectionId.incomingEmailMessages,
      anyEmailAttachments
    ),
    [ReportSectionId.workflowDecisions]: messageAttachmentsAvailability(
      ReportSectionId.workflowDecisions,
      false
    ),
  };
}

export function disableMessageAttachmentsConfig(
  config: MessageSectionConfigOnView
): MessageSectionConfigOnView {
  return {
    ...config,
    attachments: { ...config.attachments, enabled: false },
  };
}

export function shouldDisableMessageAttachments(
  config: MessageSectionConfigOnView | undefined,
  attachmentsAvailable: boolean
): config is MessageSectionConfigOnView {
  return Boolean(config && !attachmentsAvailable && config.attachments.enabled);
}

export type SingleIssueReportAvailability = {
  sections: ReportSectionAvailabilityMap;
  messageAttachments: ReportMessageAttachmentsAvailabilityMap;
};

/** What the report of an issue can contain; all of it until the issue is there, so saved sections stay enabled. */
export function computeSingleIssueReportAvailability(
  issue: IssueOnSingleView | undefined,
  documents: DocumentsOnIssueView
): SingleIssueReportAvailability {
  if (!issue) {
    return {
      sections: allSectionsAvailable(),
      messageAttachments: allMessageAttachmentsAvailable(),
    };
  }
  return {
    sections: computeReportSectionAvailability([issue], true, documents.issueDocuments.length),
    messageAttachments: computeMessageAttachmentsAvailabilityFromDocuments(documents),
  };
}

export type SingleIssueReportEstimate = {
  issueCount: number;
  issueRows: ReportIssueEstimateRow[];
  isLoading: boolean;
};

/** The rows the page estimate of an issue's report counts; loading until the issue is read. */
export function computeSingleIssueReportEstimate(
  issueModel: IssueModel | undefined,
  documents: DocumentsOnIssueView
): SingleIssueReportEstimate {
  if (!issueModel) {
    // Not an estimate from no rows, which would be misleadingly small.
    return { issueCount: 1, issueRows: [], isLoading: true };
  }
  return {
    issueCount: 1,
    issueRows: buildReportIssueEstimateRows(
      [issueModel],
      [buildReportIssueStatsOverridesFromDocuments(documents)]
    ),
    isLoading: false,
  };
}
